// ============================================
// Financial Data Hook
// Fetches orders and computes all financial metrics
// ============================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import { getAllOrders, vendorAuthenticate } from '../../../services/firestore/orderService';
import type { Order, OrderStatus } from '../../../types/order';

// --- Exported types ---

export interface DateRange {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

export interface TimeSeriesEntry {
  period: string;
  revenueCents: number;
  orderCount: number;
}

export interface BundleBreakdown {
  bundleName: string;
  revenueCents: number;
  orderCount: number;
  pctOfTotal: number;
}

export interface PipelineEntry {
  status: OrderStatus;
  count: number;
  pctOfTotal: number;
}

export interface FinancialMetrics {
  totalRevenueCents: number;
  orderCount: number;
  avgOrderValueCents: number;
  conversionRate: number;
  providerRevenueCents: number;
  providerOrderCount: number;
  revenueByDay: TimeSeriesEntry[];
  revenueByWeek: TimeSeriesEntry[];
  revenueByMonth: TimeSeriesEntry[];
  revenueByBundle: BundleBreakdown[];
  pipeline: PipelineEntry[];
  filteredOrders: Order[];
  allOrders: Order[];
}

// --- Helpers ---

const REVENUE_STATUSES: OrderStatus[] = ['paid', 'processing', 'shipped', 'delivered'];

const STATUS_PIPELINE_ORDER: OrderStatus[] = [
  'pending',
  'paid',
  'processing',
  'shipped',
  'delivered',
  'cancelled',
];

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultDateRange(): DateRange {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: todayStr(),
  };
}

/** Floor a YYYY-MM-DD date string to the Monday of that week */
function floorToMonday(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay(); // 0 = Sunday, 1 = Monday, ...
  const diff = day === 0 ? 6 : day - 1; // days since Monday
  d.setDate(d.getDate() - diff);
  return d.toISOString().slice(0, 10);
}

function getOrderDate(order: Order): string {
  return (order.paidAt || order.createdAt).slice(0, 10);
}

// --- Hook ---

export function useFinancialData() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAuthRequired(false);
      const data = await getAllOrders();
      setOrders(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load orders';
      if (msg.includes('not authenticated') || msg.includes('session expired')) {
        setAuthRequired(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const authenticate = useCallback(
    async (password: string) => {
      await vendorAuthenticate(password);
      await loadOrders();
    },
    [loadOrders],
  );

  const metrics = useMemo<FinancialMetrics>(() => {
    // 1. Filter by date range
    const inRange = orders.filter((o) => {
      const d = getOrderDate(o);
      return d >= dateRange.from && d <= dateRange.to;
    });

    // 2. Revenue orders = those with revenue-bearing statuses
    const revenueOrders = inRange.filter((o) =>
      REVENUE_STATUSES.includes(o.status),
    );

    // 3. Core KPIs
    const totalRevenueCents = revenueOrders.reduce(
      (sum, o) => sum + o.totalInCents,
      0,
    );
    const orderCount = revenueOrders.length;
    const avgOrderValueCents =
      orderCount > 0 ? Math.round(totalRevenueCents / orderCount) : 0;

    // Conversion rate: revenue orders / all orders in range
    const conversionRate =
      inRange.length > 0 ? orderCount / inRange.length : 0;

    // 4. Provider revenue (requiresProvider orders at $49 each)
    const providerOrders = revenueOrders.filter((o) => o.requiresProvider);
    const providerOrderCount = providerOrders.length;
    const providerRevenueCents = providerOrderCount * 4900;

    // 5. Revenue by day
    const dayMap = new Map<string, { rev: number; count: number }>();
    for (const o of revenueOrders) {
      const d = getOrderDate(o);
      const existing = dayMap.get(d) || { rev: 0, count: 0 };
      existing.rev += o.totalInCents;
      existing.count += 1;
      dayMap.set(d, existing);
    }
    const revenueByDay: TimeSeriesEntry[] = Array.from(dayMap.entries())
      .map(([period, v]) => ({
        period,
        revenueCents: v.rev,
        orderCount: v.count,
      }))
      .sort((a, b) => a.period.localeCompare(b.period));

    // 6. Revenue by week (Monday-start)
    const weekMap = new Map<string, { rev: number; count: number }>();
    for (const o of revenueOrders) {
      const d = getOrderDate(o);
      const week = floorToMonday(d);
      const existing = weekMap.get(week) || { rev: 0, count: 0 };
      existing.rev += o.totalInCents;
      existing.count += 1;
      weekMap.set(week, existing);
    }
    const revenueByWeek: TimeSeriesEntry[] = Array.from(weekMap.entries())
      .map(([period, v]) => ({
        period,
        revenueCents: v.rev,
        orderCount: v.count,
      }))
      .sort((a, b) => a.period.localeCompare(b.period));

    // 7. Revenue by month
    const monthMap = new Map<string, { rev: number; count: number }>();
    for (const o of revenueOrders) {
      const d = getOrderDate(o);
      const month = d.slice(0, 7); // YYYY-MM
      const existing = monthMap.get(month) || { rev: 0, count: 0 };
      existing.rev += o.totalInCents;
      existing.count += 1;
      monthMap.set(month, existing);
    }
    const revenueByMonth: TimeSeriesEntry[] = Array.from(monthMap.entries())
      .map(([period, v]) => ({
        period,
        revenueCents: v.rev,
        orderCount: v.count,
      }))
      .sort((a, b) => a.period.localeCompare(b.period));

    // 8. Bundle breakdown
    const bundleMap = new Map<string, { rev: number; count: number }>();
    for (const o of revenueOrders) {
      const name = o.bundleName || 'No Bundle';
      const existing = bundleMap.get(name) || { rev: 0, count: 0 };
      existing.rev += o.totalInCents;
      existing.count += 1;
      bundleMap.set(name, existing);
    }
    const revenueByBundle: BundleBreakdown[] = Array.from(bundleMap.entries())
      .map(([bundleName, v]) => ({
        bundleName,
        revenueCents: v.rev,
        orderCount: v.count,
        pctOfTotal:
          totalRevenueCents > 0
            ? Math.round((v.rev / totalRevenueCents) * 1000) / 10
            : 0,
      }))
      .sort((a, b) => b.revenueCents - a.revenueCents);

    // 9. Pipeline (all orders in range, all statuses)
    const statusCounts = new Map<OrderStatus, number>();
    for (const o of inRange) {
      statusCounts.set(o.status, (statusCounts.get(o.status) || 0) + 1);
    }
    const pipeline: PipelineEntry[] = STATUS_PIPELINE_ORDER.map((status) => {
      const count = statusCounts.get(status) || 0;
      return {
        status,
        count,
        pctOfTotal:
          inRange.length > 0
            ? Math.round((count / inRange.length) * 1000) / 10
            : 0,
      };
    });

    return {
      totalRevenueCents,
      orderCount,
      avgOrderValueCents,
      conversionRate,
      providerRevenueCents,
      providerOrderCount,
      revenueByDay,
      revenueByWeek,
      revenueByMonth,
      revenueByBundle,
      pipeline,
      filteredOrders: inRange,
      allOrders: orders,
    };
  }, [orders, dateRange]);

  return {
    metrics,
    loading,
    error,
    authRequired,
    dateRange,
    setDateRange,
    authenticate,
  };
}
